import React from 'react';
import { CheckCircle, Mail, MessageCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';

interface Step12ThankYouProps {
  onNext?: () => void;
}

const Step12ThankYou: React.FC<Step12ThankYouProps> = ({ onNext }) => {
  const handleWhatsAppChat = () => {
    const phoneNumber = '+2348039260734';
    const message = encodeURIComponent('Hi! I just completed the LovaBuild onboarding. Looking forward to getting started!');
    const whatsappUrl = `https://wa.me/${phoneNumber.replace('+', '')}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-white overflow-hidden relative">
      {/* Remove global body padding for onboarding */}
      <style>{`
        body { padding-top: 0 !important; }
      `}</style>
      
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-white to-blue-50"></div>
      <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-green-400/10 to-blue-400/10 rounded-full blur-xl"></div>
      <div className="absolute bottom-20 right-10 w-40 h-40 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-xl"></div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="relative z-10 min-h-screen flex flex-col"
      >
        {/* Header */}
        <div className="p-6 text-center">
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="font-montserrat font-bold text-2xl text-nocode-blue"
          >
            LovaBuild
          </motion.div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          {/* Success Icon */}
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
            className="mb-8 relative"
          >
            <div className="w-32 h-32 mx-auto bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-2xl relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
              <CheckCircle className="h-16 w-16 text-white relative z-10" />
              
              {/* Animated sparkles */}
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0"
              >
                <Sparkles className="absolute top-2 right-6 h-4 w-4 text-yellow-300" />
                <Sparkles className="absolute bottom-6 left-2 h-3 w-3 text-blue-300" />
                <Sparkles className="absolute top-8 left-8 h-2 w-2 text-purple-300" />
              </motion.div>
            </div>
          </motion.div>

          {/* Thank You Message */}
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-montserrat font-bold mb-4 leading-tight text-gray-900 max-w-4xl"
          >
            🎉 Welcome to{' '}
            <span className="bg-gradient-to-r from-green-500 to-blue-600 bg-clip-text text-transparent">
              LovaBuild
            </span>!
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl leading-relaxed font-poppins"
          >
            Your payment has been processed successfully! You're now ready to start building amazing projects with AI.
          </motion.p>

          {/* Email Instructions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
            className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-2xl p-6 mb-8 max-w-lg w-full"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-green-500 rounded-full flex items-center justify-center">
                <Mail className="h-5 w-5 text-white" />
              </div>
              <h3 className="font-montserrat font-semibold text-gray-900">Check Your Email</h3>
            </div>
            <p className="text-gray-600 font-poppins text-sm leading-relaxed">
              We've sent detailed instructions and your course access details to your email. 
              Please check your inbox (and spam folder) for next steps.
            </p>
          </motion.div>

          {/* WhatsApp Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
            className="mb-8"
          >
            <p className="text-gray-600 font-poppins mb-4">
              Have questions? Need immediate support?
            </p>
            <Button
              onClick={handleWhatsAppChat}
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-montserrat font-semibold px-6 py-3 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl group"
            >
              <MessageCircle className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
              Chat on WhatsApp
            </Button>
          </motion.div>

          {/* Next Steps */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4 }}
            className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-2xl p-6 max-w-lg w-full"
          >
            <h3 className="font-montserrat font-semibold text-gray-900 mb-3">What's Next?</h3>
            <ul className="text-left text-gray-600 font-poppins text-sm space-y-2">
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                <span>Check your email for course access and login details</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                <span>Join our community for support and updates</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <span>Start building your first AI-powered project!</span>
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Footer */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="p-6 text-center text-gray-400 text-sm font-poppins"
        >
          <p>Thank you for choosing LovaBuild. Let's build something amazing together! 🚀</p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Step12ThankYou;